/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { isEmpty, keyBy, noop } from 'lodash';
import { GanttTimelineLink } from '../resources/gantt.model';

export function ganttDelayRendering(gantt: GanttStatic, op: () => void): void {
  const originalRender = gantt.render;
  try {
    gantt.render = noop;
    op();
  } finally {
    gantt.render = originalRender;
    gantt.render();
  }
}

export function ganttValidateLinks(gantt: any, taskId: ID, newLinks: GanttTimelineLink[]): GanttTimelineLink[] {
  let allRelations = gantt.getLinks().filter(({ target }: GanttTimelineLink) => target !== taskId);
  newLinks.forEach((newLink) => {
    if (!gantt.isLinkExists(newLink.id)) {
      allRelations = allRelations.concat(gantt._formatLink(newLink));
    }
  });
  const circularLinks: GanttTimelineLink[] = [];
  const checkedTargets: string[] = [];

  const recursive = (target: string, newRootLink: GanttTimelineLink, peaks: string[]): void => {
    if (circularLinks.length || checkedTargets.includes(target)) {
      return;
    }
    checkedTargets.push(target);
    
    const arrayRelations = allRelations.filter((link: GanttTimelineLink) => link.target === target);
    arrayRelations.forEach((link: GanttTimelineLink) => {
      const peaksForBranch = [...peaks];
      if (peaksForBranch.includes(link.source)) {
        if (!circularLinks.find((item) => item.id === newRootLink.id)) {
          circularLinks.push(newRootLink);
        }
        return;
      }
      peaksForBranch.push(link.source);
      recursive(link.source, newRootLink, peaksForBranch);
    });
  };

  const peaks: string[] = [];
  newLinks.forEach((newLink) => {
    peaks.push(newLink.target);
    peaks.push(newLink.source);
    recursive(newLink.source, newLink, peaks);
  });

  if (circularLinks.length) {
    return circularLinks;
  }

  const newLinkIndex = keyBy(newLinks, 'id');
  const newInvalidLinkIndex: { [key: string]: boolean } = {};
  const loops = gantt.findCycles();
  loops.forEach(({ links }: { links: any[] }) => {
    links.forEach((link) => {
      if (newLinkIndex[link]) {
        newInvalidLinkIndex[link] = true;
      }
    });
  });
  if (isEmpty(newInvalidLinkIndex)) {
    return [];
  } else {
    return Object.keys(newInvalidLinkIndex)
                 .map(Number)
                 .map((id) => <GanttTimelineLink>newLinks.find((link) => Number(link.id) === id));
  }
}

export function ganttEnsureTaskVisible(gantt: any, taskId: ID): void {
  const taskPosition = gantt.getTaskPosition(gantt.getTask(taskId));

  const taskHeight = gantt.config.row_height;
  const left = Math.max(taskPosition.left - gantt.config.task_scroll_offset, 0);

  const { y: viewportHeight, y_pos: viewportTop } = gantt._scroll_state();
  if (!viewportHeight) {
    // if height of viewport is unknown => try to scroll to the top of the task
    gantt.scrollTo(left, taskPosition.top);
  } else if (taskPosition.top < viewportTop) {
    // if task above of viewport => scroll to top
    gantt.scrollTo(left, taskPosition.top);
  } else if (taskPosition.top + taskHeight > viewportTop + viewportHeight) {
    // if task below than viewport => scroll to make task visible in the bottom
    gantt.scrollTo(left, taskPosition.top + taskHeight - viewportHeight);
  } else {
    // task is visible => vertical scroll is not required
    gantt.scrollTo(left, viewportTop);
  }
}
